import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Sparkles, ChevronDown, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { track } from "@/lib/analytics";
import {
  supabase,
  type ClothingItem,
  type JewelryItem,
} from "@/lib/supabase";
import {
  scoreItemForSelection,
  type Season,
  weatherAttrFor,
  isStalenessSurfaced,
  withFeelingNote,
} from "@/lib/style-matching";
import { AppShell } from "@/components/app-shell";
import { SectionLabel } from "@/components/ui-extras";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SmartMultiSelect, FEELING_PRESETS } from "@/components/smart-select";
import { ImageBox } from "@/components/ui-extras";
import { ItemPickerSheet, type PickItem } from "@/components/item-picker-sheet";
import { WeatherChips } from "@/components/weather-chips";
import { WeatherIndicator } from "@/components/weather-indicator";
import { UpgradePromptSheet } from "@/components/upgrade-prompt-sheet";
import { useTotalCollectionCount } from "@/components/collection-prompts";
import {
  getLiveWeather,
  getManualWeather,
  type WeatherBand,
  type WeatherCondition,
  type WeatherContext,
} from "@/lib/weather";
import {
  type CompletenessExtra,
  type CatalogItem,
  type SelectionContext,
  type ScannedPiece,
  type SelectionInputs,
  type SelectionResult,
  type GenerationInputs,
  buildCatalogItems,
  buildSelectionContext,
  runAutoSelection,
  writeBackSurfaced,
  generateAndPersist,
} from "@/lib/style-selection";

const LILAC = "var(--caileve-lilac)";
const GOLD = "var(--caileve-gold)";
const INK = "var(--caileve-ink)";
const TAUPE = "var(--caileve-muted)";
const IVORY = "var(--caileve-bg)";



export const Route = createFileRoute("/suggest")({
  head: () => ({
    meta: [
      { title: "Style Me — Cailève" },
      { name: "description", content: "Request a styling suggestion from your concierge." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): {
    jewelryId?: string;
    clothingId?: string;
    accessoryId?: string;
  } => ({
    jewelryId: typeof search.jewelryId === "string" ? search.jewelryId : undefined,
    clothingId: typeof search.clothingId === "string" ? search.clothingId : undefined,
    accessoryId: typeof search.accessoryId === "string" ? search.accessoryId : undefined,
  }),
  component: () => (
    <AppShell>
      <SuggestPage />
    </AppShell>
  ),
});

const BASE_EVENTS = [
  "Dinner",
  "Brunch",
  "Party",
  "Black Tie",
  "Work",
  "Casual",
  "Travel",
  "Celebration",
];
const SEASONS = ["Spring", "Summer", "Fall", "Winter", "All Seasons"];
const TIMES = ["Morning", "Afternoon", "Evening"];



function SuggestPage() {
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const navigate = useNavigate();
  const { data: totalCount } = useTotalCollectionCount();
  const scannedPiece = (() => {
    try {
      if (typeof window === "undefined") return null;
      const raw = sessionStorage.getItem("caileve_scanned_piece");
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        description: string;
        category: string;
        verdict_type: string;
        photoPath: string | null;
        occasion: string | null;
        timeframe: string | null;
      };
      return parsed;
    } catch {
      return null;
    }
  })();
  const [scannedDismissed, setScannedDismissed] = useState(false);
  const activePiece = scannedDismissed ? null : scannedPiece;

  const [event, setEvent] = useState(scannedPiece?.occasion ?? "");
  const [season, setSeason] = useState(() => {
    const month = new Date().getMonth();
    if (month <= 1 || month === 11) return "Winter";
    if (month <= 4) return "Spring";
    if (month <= 7) return "Summer";
    return "Fall";
  });
  const [wantToFeel, setWantToFeel] = useState<string[]>([]);
  const [time, setTime] = useState(() => {
    const h = new Date().getHours();
    if (h < 12) return "Morning";
    if (h < 17) return "Afternoon";
    return "Evening";
  });
  const [jewelryId, setJewelryId] = useState("");
  const [clothingId, setClothingId] = useState("");
  const [accessoryId, setAccessoryId] = useState("");
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [weather, setWeather] = useState<WeatherContext | null>(null);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [piecesExpanded, setPiecesExpanded] = useState(false);
  const [styleMeLimitOpen, setStyleMeLimitOpen] = useState(false);

  useEffect(() => {
    if (activePiece) setPiecesExpanded(true);
  }, [activePiece]);

  const search = Route.useSearch();
  useEffect(() => {
    if (search.jewelryId) { setJewelryId(search.jewelryId); setPiecesExpanded(true); }
    if (search.clothingId) { setClothingId(search.clothingId); setPiecesExpanded(true); }
    if (search.accessoryId) { setAccessoryId(search.accessoryId); setPiecesExpanded(true); }
  }, [search.jewelryId, search.clothingId, search.accessoryId]);

  // Attempt live weather once on mount; if usable, prefill chips.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const w = await getLiveWeather();
      if (cancelled) return;
      if (w && w.source !== "unavailable") setWeather(w);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const currentBand: WeatherBand | null =
    weather && weather.source !== "unavailable" ? weather.band : null;
  const currentCondition: WeatherCondition | null =
    weather && weather.source !== "unavailable" ? weather.condition : null;

  const handleBandChange = async (b: WeatherBand) => {
    const w = await getManualWeather(b, currentCondition ?? undefined);
    if (w) setWeather(w);
  };
  const handleConditionChange = async (c: WeatherCondition | null) => {
    const band = currentBand ?? "mild";
    const w = await getManualWeather(band, c ?? undefined);
    if (w) setWeather(w);
  };

  const { data: customEventData } = useQuery({
    queryKey: ["user_field_options", uid, "event"],
    enabled: !!uid,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_field_options")
        .select("custom_value")
        .eq("user_id", uid)
        .eq("field_name", "event");
      return (data ?? []).map((r) => (r as { custom_value: string }).custom_value);
    },
  });

  const { data: lastRequestEvent } = useQuery({
    queryKey: ["last_style_request", uid],
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("style_requests")
        .select("event")
        .eq("user_id", uid)
        .not("event", "is", null)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();
      return data?.event ?? null;
    },
  });

  useEffect(() => {
    if (lastRequestEvent && !event) setEvent(lastRequestEvent);
  }, [lastRequestEvent]);

  const allEvents = [
    ...BASE_EVENTS,
    ...(customEventData ?? []).filter((e) => !BASE_EVENTS.includes(e)),
  ];

  const { data: jewelry } = useQuery({
    queryKey: ["jewelry_items_for_styling", uid],
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      try {
        const { data } = await supabase
          .from("jewelry_items")
          .select("*")
          .eq("user_id", uid)
          .eq("is_accessory", false)
          .eq("is_wishlist", false);
        return (data ?? []) as JewelryItem[];
      } catch {
        return [] as JewelryItem[];
      }
    },
  });
  const { data: clothing } = useQuery({
    queryKey: ["clothing_items_for_styling", uid],
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      try {
        const { data } = await supabase.from("clothing_items").select("*").eq("user_id", uid).eq("is_wishlist", false);
        return (data ?? []) as ClothingItem[];
      } catch {
        return [] as ClothingItem[];
      }
    },
  });
  const { data: accessories } = useQuery({
    queryKey: ["accessory_items_for_styling", uid],
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
    queryFn: async () => {
      try {
        const { data } = await supabase
          .from("jewelry_items")
          .select("*")
          .eq("user_id", uid)
          .eq("is_accessory", true)
          .eq("is_wishlist", false);
        return (data ?? []) as JewelryItem[];
      } catch {
        return [] as JewelryItem[];
      }
    },
  });

  const jewelryItems: PickItem[] = useMemo(
    () => (jewelry ?? []).filter((j) => !j.is_wishlist).map((j) => ({
      id: j.id,
      name: j.name,
      photo_url: j.photo_url,
      caption: j.category,
      photo_url_no_bg: (j as { photo_url_no_bg?: string | null }).photo_url_no_bg ?? null,
      prefer_clean_bg: (j as { prefer_clean_bg?: boolean | null }).prefer_clean_bg ?? null,
      clean_bg_color: (j as { clean_bg_color?: string | null }).clean_bg_color ?? null,
    })),
    [jewelry],
  );
  const clothingItems: PickItem[] = useMemo(
    () => (clothing ?? []).filter((c) => !c.is_wishlist).map((c) => ({
      id: c.id,
      name: c.name,
      photo_url: c.photo_url,
      caption: c.clothing_type,
      photo_url_no_bg: (c as { photo_url_no_bg?: string | null }).photo_url_no_bg ?? null,
      prefer_clean_bg: (c as { prefer_clean_bg?: boolean | null }).prefer_clean_bg ?? null,
      clean_bg_color: (c as { clean_bg_color?: string | null }).clean_bg_color ?? null,
    })),
    [clothing],
  );
  const accessoryItems: PickItem[] = useMemo(
    () => (accessories ?? []).filter((a) => !a.is_wishlist).map((a) => ({
      id: a.id,
      name: a.name,
      photo_url: a.photo_url,
      caption: a.accessory_type ?? null,
      photo_url_no_bg: (a as { photo_url_no_bg?: string | null }).photo_url_no_bg ?? null,
      prefer_clean_bg: (a as { prefer_clean_bg?: boolean | null }).prefer_clean_bg ?? null,
      clean_bg_color: (a as { clean_bg_color?: string | null }).clean_bg_color ?? null,
    })),
    [accessories],
  );

  const selectedJewelry = jewelry?.find((j) => j.id === jewelryId) ?? null;
  const selectedClothing = clothing?.find((c) => c.id === clothingId) ?? null;
  const selectedAccessory = accessories?.find((a) => a.id === accessoryId) ?? null;

  // fuzzyMatchPiece imported from @/lib/style-matching

  const m = useMutation({
    mutationFn: async () => {
      const jw = selectedJewelry;
      const cl = selectedClothing;
      const ac = selectedAccessory;

      const anchorFlags = {
        jewelry: jw !== null,
        clothing: cl !== null,
        accessory: ac !== null,
      };

      const currentMonth = new Date().getUTCMonth();
      const currentSeason: Season =
        currentMonth <= 1 || currentMonth === 11 ? "winter"
        : currentMonth <= 4 ? "spring"
        : currentMonth <= 7 ? "summer" : "fall";

      // Run the five phases in dependency order. Phases 1 and 2
      // are independent and run in parallel.
      const [catalogItems, selectionCtx] = await Promise.all([
        buildCatalogItems(uid),
        buildSelectionContext(uid),
      ]);

      const selectionResult = await runAutoSelection(
        jw, cl, ac,
        {
          uid,
          discoveryMode,
          wantToFeel,
          weather,
          scannedCategory: activePiece?.category ?? null,
          currentMonth,
          currentSeason,
          classificationCtx: selectionCtx.classificationCtx,
          recentlyUsedIds: selectionCtx.recentlyUsedIds,
          event,
          archetypeProfile: selectionCtx.archetypeProfile,
          feedbackStats: selectionCtx.feedbackStats,
        }
      );

      await writeBackSurfaced(
        uid,
        selectionResult.effectiveJewelry,
        selectionResult.effectiveClothing,
        selectionResult.effectiveAccessory,
        selectionResult.jewelryPickScore,
        selectionResult.clothingPickScore,
        selectionResult.accessoryPickScore,
        selectionResult.completenessExtras,
      );

      return generateAndPersist(
        selectionResult.effectiveJewelry,
        selectionResult.effectiveClothing,
        selectionResult.effectiveAccessory,
        selectionResult.jewelryPickScore,
        selectionResult.clothingPickScore,
        selectionResult.accessoryPickScore,
        {
          uid,
          event,
          season,
          time,
          wantToFeel,
          discoveryMode,
          weather,
          activePiece,
          catalogItems,
          completenessExtras: selectionResult.completenessExtras,
          clothingTradeoffNotes: selectionResult.clothingTradeoffNotes,
          jewelryTradeoffNotes: selectionResult.jewelryTradeoffNotes,
          accessoryTradeoffNotes: selectionResult.accessoryTradeoffNotes,
          anchorFlags,
          archetypeProfile: selectionCtx.archetypeProfile,
          jewelry: jewelry ?? [],
          clothing: clothing ?? [],
          accessories: accessories ?? [],
        }
      );
    },
    onSuccess: (id) => {
      track("style_me_requested");
      navigate({ to: "/requests/$id", params: { id } });
    },
    onError: (e: Error) => {
      if (e.message === "rate_limit_reached") {
        setStyleMeLimitOpen(true);
        track("upgrade_prompted");
        return;
      }
      toast.error(e.message);
    },
  });

  useEffect(() => {
    return () => {
      if (m.status !== "success") {
        try {
          sessionStorage.removeItem("caileve_scanned_piece");
        } catch {}
      }
    };
  }, [m.status]);


  const steps = [
    event !== "",
    season !== "" || time !== "",
    wantToFeel.length > 0,
    piecesExpanded || discoveryMode,
  ];
  const completedSteps = steps.filter(Boolean).length;

  const selectedParts = [event, season, time].filter(Boolean);
  const missingParts: string[] = [];
  if (!event) missingParts.push("Event");
  if (!season && !time) missingParts.push("Season/Time");
  if (wantToFeel.length === 0) missingParts.push("Feeling");

  return (
    <div className="max-w-3xl mx-auto px-8 pt-10 pb-32">
      <SectionLabel>Editorial Concierge</SectionLabel>
      <div className="flex items-start justify-between gap-4 mt-3">
        <h1 className="font-serif-display text-4xl">Style Me</h1>
        <Link
          to="/requests"
          className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground whitespace-nowrap mt-3"
        >
          Previous Styles
        </Link>
      </div>
      <p className="font-serif-display italic text-xl text-muted-foreground mt-4">
        Tell me about the moment.
      </p>

      {totalCount !== undefined && totalCount < 3 && (
        <div
          style={{
            borderLeft: "2px solid var(--caileve-gold)",
            backgroundColor: "rgba(198,166,107,0.06)",
            padding: "var(--caileve-space-5) var(--caileve-space-6)",
            marginBottom: "24px",
            marginTop: "16px",
          }}
        >
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: 15,
              color: "var(--caileve-ink)",
              margin: 0,
            }}
          >
            Style Me works best with a few pieces in your collection. Add what you are wearing today and Cailève will begin learning your style.
          </p>
          <Link
            to="/jewelry"
            search={{ new: 1 }}
            style={{
              display: "inline-block",
              marginTop: 8,
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--caileve-lilac-text)",
            }}
          >
            Add your first piece
          </Link>
        </div>
      )}

      <div className="flex items-center gap-2 mt-6 mb-2">
        {steps.map((done, i) => (
          <div
            key={i}
            style={{
              height: 2,
              flex: 1,
              borderRadius: "var(--caileve-radius-pill)",
              backgroundColor: done ? "var(--caileve-lilac)" : "var(--caileve-soft)",
              transition: "background-color 300ms ease",
            }}
          />
        ))}
        <span
          className="text-[10px] uppercase tracking-[0.2em] ml-2 shrink-0"
          style={{ color: "var(--caileve-muted)" }}
        >
          {selectedParts.length === 0
            ? "Tell Cailève the moment to begin"
            : missingParts.length === 0
              ? `${selectedParts.join(" · ")} · moment complete`
              : `${selectedParts.join(" · ")} · ${missingParts.join(", ")} needed`}
        </span>
      </div>

      {activePiece && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-sm"
          style={{ backgroundColor: "var(--caileve-soft)", borderLeft: `3px solid ${LILAC}` }}
        >
          <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: LILAC }} />
          <p
            className="italic flex-1"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 14,
              color: INK,
              margin: 0,
            }}
          >
            Styling with: {activePiece.description}
          </p>
          <button
            type="button"
            onClick={() => {
              setScannedDismissed(true);
              sessionStorage.removeItem("caileve_scanned_piece");
              setEvent("");
            }}
            aria-label="Remove scanned piece"
            className="flex-shrink-0 hover:opacity-60 transition-opacity"
            style={{ color: LILAC }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid gap-6 mt-12">
        <div>
          <FieldLabel>Event</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {allEvents.map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setEvent(o)}
                className={
                  "px-5 py-3 text-sm border transition-colors " +
                  (event === o
                    ? "border-transparent text-[var(--caileve-bg)]"
                    : "border-[rgba(198,166,107,0.25)] text-[var(--caileve-muted)] hover:border-[var(--caileve-lilac)] hover:text-[var(--caileve-ink)]")
                }
                style={{ backgroundColor: event === o ? "var(--caileve-lilac)" : "transparent", borderRadius: "var(--caileve-radius-md)" }}
              >
                {o}
              </button>
            ))}
            <AddEventButton userId={uid} onAdded={(e) => setEvent(e)} />
          </div>
        </div>
        {!activePiece && (
          <>
            <div style={{ height: "0.5px", backgroundColor: "var(--caileve-soft)", margin: "8px 0" }} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 0",
                borderBottom: "1px solid var(--caileve-soft)",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: discoveryMode ? "var(--caileve-ink)" : "var(--caileve-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.25em",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontStyle: "italic",
                  transition: "color 0.2s",
                }}
              >
                {discoveryMode
                  ? "Discovering my full collection"
                  : "Use my go-to pieces"}
              </span>
              <button
                type="button"
                onClick={() => setDiscoveryMode((d) => !d)}
                style={{
                  width: 40,
                  height: 22,
                  borderRadius: 11,
                  backgroundColor: discoveryMode ? "var(--caileve-gold)" : "var(--caileve-soft)",
                  border: "none",
                  cursor: "pointer",
                  position: "relative",
                  transition: "background-color 0.2s",
                  flexShrink: 0,
                }}
                aria-label="Toggle discovery mode"
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    position: "absolute",
                    top: 3,
                    left: discoveryMode ? 21 : 3,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
              </button>
            </div>
            <div style={{ height: "0.5px", backgroundColor: "var(--caileve-soft)", margin: "8px 0" }} />
            <div>
              <FieldLabel>When?</FieldLabel>
              <div className="flex flex-wrap gap-2 mb-3">
                {SEASONS.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setSeason((s) => (s === o ? "" : o))}
                    className={
                      "px-4 py-2 text-sm border transition-colors " +
                      (season === o
                        ? "border-transparent text-[var(--caileve-bg)]"
                        : "border-[rgba(198,166,107,0.25)] text-[var(--caileve-muted)] hover:border-[var(--caileve-lilac)] hover:text-[var(--caileve-ink)]")
                    }
                    style={{ backgroundColor: season === o ? "var(--caileve-lilac)" : "transparent", borderRadius: "var(--caileve-radius-md)" }}
                  >
                    {o}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {TIMES.map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setTime((t) => (t === o ? "" : o))}
                    className={
                      "px-4 py-2 text-sm border transition-colors " +
                      (time === o
                        ? "border-transparent text-[var(--caileve-bg)]"
                        : "border-[rgba(198,166,107,0.25)] text-[var(--caileve-muted)] hover:border-[var(--caileve-lilac)] hover:text-[var(--caileve-ink)]")
                    }
                    style={{ backgroundColor: time === o ? "var(--caileve-lilac)" : "transparent", borderRadius: "var(--caileve-radius-md)" }}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <FieldLabel>How do you want to feel?</FieldLabel>
              <SmartMultiSelect
                userId={uid}
                fieldName="want_to_feel"
                presets={FEELING_PRESETS}
                values={wantToFeel}
                onChange={setWantToFeel}
                max={3}
                placeholder="Choose up to 3 feelings…"
              />
            </div>

            <div>
              {weather && weather.source !== "unavailable" && (
                <div className="flex items-center justify-between">
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <WeatherIndicator condition={currentCondition} band={currentBand} />
                    <p
                      className="font-serif-display italic text-sm"
                      style={{ color: "var(--caileve-muted)" }}
                    >
                      {`Styled for ${weather.summary.toLowerCase()} weather`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setWeatherExpanded((e) => !e)}
                    className="text-[10px] uppercase tracking-[0.2em] hover:opacity-80"
                    style={{ color: "var(--caileve-lilac-text)" }}
                  >
                    {weatherExpanded ? "Done" : "Adjust"}
                  </button>
                </div>
              )}
              {(!weather || weather.source === "unavailable" || weatherExpanded) && (
                <div className={weather && weather.source !== "unavailable" ? "mt-3" : ""}>
                  <WeatherChips
                    band={currentBand}
                    condition={currentCondition}
                    onBandChange={handleBandChange}
                    onConditionChange={handleConditionChange}
                  />
                </div>
              )}
            </div>

            <div style={{ height: "0.5px", backgroundColor: "var(--caileve-soft)", margin: "8px 0" }} />
            <div>
              <button
                type="button"
                onClick={() => setPiecesExpanded((e) => !e)}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                style={{ color: "var(--caileve-lilac-text)" }}
              >
                <span
                  className="font-serif-display italic text-base"
                  style={{ color: "var(--caileve-lilac-text)" }}
                >
                  {piecesExpanded ? "Let Cailève choose for me" : "Start with a specific piece"}
                </span>
              </button>
              {piecesExpanded && (
                <div className="flex flex-col gap-3 mt-4">
                  <PreferredPicker
                    label="Jewelry"
                    sheetTitle="Choose Jewelry"
                    items={jewelryItems}
                    selected={selectedJewelry ? { id: selectedJewelry.id, name: selectedJewelry.name, photo_url: selectedJewelry.photo_url } : null}
                    onSelect={(id) => setJewelryId(id ?? "")}
                  />
                  <PreferredPicker
                    label="Clothing"
                    sheetTitle="Choose Clothing"
                    items={clothingItems}
                    selected={selectedClothing ? { id: selectedClothing.id, name: selectedClothing.name, photo_url: selectedClothing.photo_url } : null}
                    onSelect={(id) => setClothingId(id ?? "")}
                  />
                  <PreferredPicker
                    label="Accessories"
                    sheetTitle="Choose Accessories"
                    items={accessoryItems}
                    selected={selectedAccessory ? { id: selectedAccessory.id, name: selectedAccessory.name, photo_url: selectedAccessory.photo_url } : null}
                    onSelect={(id) => setAccessoryId(id ?? "")}
                  />
                </div>
              )}
            </div>
          </>
        )}

        <Button
          onClick={() => {
            if (!event) return toast.error("Choose an occasion to continue.");
            m.mutate();
          }}
          disabled={m.isPending}
          className="h-12 mt-4"
          style={{ backgroundColor: "var(--caileve-lilac)", color: "var(--caileve-bg)" }}
        >
          {m.isPending ? "Curating…" : "Build My Look"}
        </Button>
        {(jewelryItems?.length ?? 0) + (clothingItems?.length ?? 0) + (accessoryItems?.length ?? 0) < 8 && (
          <p
            className="text-center font-serif-display italic text-sm mt-3"
            style={{ color: "var(--caileve-muted)" }}
          >
            Cailève works with what you have — every piece added sharpens the intelligence.
          </p>
        )}
        {m.isPending && (
          <div
            className="mt-4 py-6 px-4 text-center"
            style={{ backgroundColor: IVORY }}
          >
            <p
              className="italic"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: TAUPE,
                fontSize: 13,
              }}
            >
              Your stylist is thinking...
            </p>
            <div
              className="mx-auto mt-3 animate-pulse"
              style={{
                height: 4,
                width: "60%",
                backgroundColor: "var(--caileve-soft)",
                borderRadius: "var(--caileve-radius-pill)",
              }}
            />
          </div>
        )}
      </div>
      <UpgradePromptSheet
        open={styleMeLimitOpen}
        onOpenChange={setStyleMeLimitOpen}
      />
    </div>
  );
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={
              "px-5 py-3 text-sm border transition-colors " +
              (value === o
                ? "border-accent bg-accent/15 text-foreground"
                : "border-border text-muted-foreground hover:border-accent/50")
            }
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground block mb-3">
      {children}
    </Label>
  );
}

function PreferredPicker({
  label,
  sheetTitle,
  items,
  selected,
  onSelect,
}: {
  label: string;
  sheetTitle: string;
  items: PickItem[];
  selected: { id: string; name: string; photo_url: string | null } | null;
  onSelect: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-3 border border-border px-3 h-10 text-sm bg-background hover:border-accent/50 transition-colors text-left rounded-md"
      >
        {selected ? (
          <span className="flex items-center gap-3 min-w-0">
            <span className="h-7 w-7 flex-shrink-0">
              <ImageBox src={selected.photo_url} alt={selected.name} aspect="aspect-square" />
            </span>
            <span className="truncate" style={{ color: INK }}>{selected.name}</span>
          </span>
        ) : (
          <span className="text-muted-foreground">Optional</span>
        )}
        <span className="flex items-center gap-2 flex-shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onSelect(null);
                }
              }}
              aria-label="Clear selection"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </span>
      </button>

      <ItemPickerSheet
        open={open}
        onOpenChange={setOpen}
        title={sheetTitle}
        items={items}
        selectedId={selected?.id ?? null}
        onSelect={(id) => {
          onSelect(id);
          setOpen(false);
        }}
      />
    </div>
  );
}

function AddEventButton({
  userId,
  onAdded,
}: {
  userId: string;
  onAdded: (event: string) => void;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState("");

  const handleAdd = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await supabase.from("user_field_options").insert({
        user_id: userId,
        field_name: "event",
        custom_value: trimmed,
      } as never);
      qc.invalidateQueries({
        queryKey: ["user_field_options", userId, "event"],
      });
      onAdded(trimmed);
      setValue("");
      setAdding(false);
    } catch {
      // Silent
    }
  };

  if (!adding) {
    return (
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="px-4 py-2 text-sm border border-dashed border-border text-muted-foreground hover:border-accent/50 transition-colors"
      >
        + Add
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
          }
          if (e.key === "Escape") {
            setAdding(false);
            setValue("");
          }
        }}
        placeholder="Event name"
        className="px-3 py-2 text-sm border border-accent/50 bg-background text-foreground outline-none"
        style={{ width: 120 }}
      />
      <button
        type="button"
        onClick={handleAdd}
        className="px-3 py-2 text-xs uppercase tracking-[0.2em] text-foreground border border-accent/50 hover:bg-accent/10"
      >
        Add
      </button>
      <button
        type="button"
        onClick={() => {
          setAdding(false);
          setValue("");
        }}
        style={{
          fontSize: 11,
          color: "var(--caileve-muted)",
          background: "none",
          border: "none",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  );
}
